/**
 * Department List Component
 * 
 * Displays a list of departments with filtering, status management, and CRUD operations.
 * Note: Search functionality is intentionally excluded as per requirements.
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import StickyFlexTable from '../common/StickyFlexTable';
import ManagementToolbar from '../common/ManagementToolbar';
import StatusToggleButton from '../common/StatusToggleButton';
import StatusToggleDialog from '../common/StatusToggleDialog';
import ThemedChip from '../common/ThemedChip';
import ThemedTooltip from '../common/ThemedTooltip';
import { useTheme } from '../../ThemeContext';
import { useDetectMyRole } from '../../custom-hooks';
import { formatPhoneNumberForList } from '../../utils/helpers/format-phone-number';
import { useStatusToggle } from '../../custom-hooks';
import { DEPARTMENT_FILTER_GROUPS, MODULE_PERMISSIONS, SEARCH_PLACEHOLDERS } from '../../constants/filter-constants';
import DepartmentService from '../../services/department-service';

/**
 * Department List Component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.departments - List of departments
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onAdd - Add new handler
 * @param {Object} props.facilityMap - Facility ID to name mapping
 * @param {Array} props.facilities - List of facilities for filtering
 * @param {Function} props.onRefresh - Refresh handler
 * @param {Object} props.selectedFilters - Current filter state
 * @param {Function} props.onFiltersChange - Filter change handler
 */
const DepartmentList = ({
  departments = [],
  loading = false,
  onEdit,
  onAdd,
  facilityMap = {},
  facilities = [],
  onRefresh,
  selectedFilters,
  onFiltersChange
}) => {
  // Search state (even though we won't show search, ManagementToolbar expects these props)
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { currentTheme, themeMode } = useTheme();
  const { role } = useDetectMyRole();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Status toggle functionality
  const {
    statusDialogOpen,
    itemToToggle,
    statusLoading,
    toggleAction,
    handleStatusToggle,
    handleStatusConfirm,
    handleStatusCancel
  } = useStatusToggle(DepartmentService, 'department', onRefresh);

  // No search functionality needed as per requirements

  // Permission checks
  const canEdit = MODULE_PERMISSIONS.department.edit.includes(role);

  // Create dynamic filter groups with facility options
  const createFilterGroups = () => {
    const facilityOptions = [
      { label: 'All Facilities', value: 'all' },
      ...facilities.map(facility => ({
        label: facility.facilityName,
        value: facility._id
      }))
    ];

    return DEPARTMENT_FILTER_GROUPS.map(group => {
      if (group.key === 'facility') {
        return { ...group, options: facilityOptions };
      }
      return group;
    });
  };

  // Filter departments based on selected filters
  const getFilteredDepartments = () => {
    return departments.filter(department => {
      // Filter out departments with inactive/unknown facility IDs
      if (!facilityMap[department.facilityId]) {
        return false;
      }
      
      // Status filter
      if (selectedFilters?.status && selectedFilters.status !== 'all') {
        if (selectedFilters.status === 'active' && department.isActive !== true) return false;
        if (selectedFilters.status === 'inactive' && department.isActive !== false) return false;
      }
      
      // Facility filter
      if (selectedFilters?.facility && selectedFilters.facility !== 'all') {
        if (department.facilityId !== selectedFilters.facility) return false;
      }
      
      return true;
    });
  };

  const displayedDepartments = getFilteredDepartments();

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedFilters]);

  /**
   * Handles department name click - navigates to department details with query parameter
   * 
   * @param {Object} department - Department object
   */
  const handleDepartmentClick = (department) => {
    console.log('Department clicked:', department);
    navigate(`/departments?departmentId=${department._id}&facilityId=${department.facilityId}`);
  };

  // Table columns configuration
  const columns = [
    {
      renderName: 'name',
      headerName: 'Department Name',
      minWidth: 180,
      fixed: true,
      renderCell: (row) => (
        <Typography 
          variant="body2" 
          onClick={() => handleDepartmentClick(row)}
          sx={{ 
            fontWeight: 500,
            color: '#ff4e3a',
            cursor: 'pointer',
            // textDecoration: 'underline',
            '&:hover': {
              color: themeMode === 'dark' ? 'white' : 'black'
            }
          }}
        >
          {row.name || 'N/A'}
        </Typography>
      ),
    },
    {
      renderName: 'facilityName',
      headerName: 'Facility',
      minWidth: 160,
      renderCell: (row) => (
        <Typography variant="body2">
          {facilityMap[row.facilityId] || 'Unknown Facility'}
        </Typography>
      ),
    },
    {
      renderName: 'faxNumber',
      headerName: 'Fax Number',
      minWidth: 140,
      renderCell: (row) => (
        <Typography variant="body2">
          {row.faxNumber ? formatPhoneNumberForList(row.faxNumber) : 'N/A'}
        </Typography>
      ),
    },
    {
      renderName: 'isActive',
      headerName: 'Status',
      minWidth: 100,
      renderCell: (row) => (
        <ThemedChip
          variant={row.isActive ? 'success' : 'warning'}
          size="small"
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </ThemedChip>
      ),
    },
    {
      id: 'action',
      label: 'Action',
      minWidth: 120,
      fixed: true,
      align: 'right',
      renderCell: (row) => (
        <>
          <ThemedTooltip 
            title={
              canEdit 
                ? "Edit department information" 
                : "Administrator privileges required to modify department records"
            }
            placement="top"
          >
            <span>
              <IconButton
                aria-label="edit"
                onClick={() => canEdit && onEdit && onEdit(row)}
                size="small"
                disabled={!canEdit}
                sx={{ 
                  color: canEdit 
                    ? currentTheme.text.primary 
                    : currentTheme.text.disabled || '#999',
                  cursor: canEdit ? 'pointer' : 'not-allowed'
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </ThemedTooltip>
          {/* <StatusToggleButton
            entity={row}
            module="department"
            onToggle={handleStatusToggle}
          /> */}
        </>
      ),
    },
  ];

  return (
    <Box>
      {/* Data Table */}
      <StickyFlexTable
        columns={columns}
        rows={displayedDepartments}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        toolbar={
          <ManagementToolbar
            module="department"
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={SEARCH_PLACEHOLDERS.department}
            filterGroups={createFilterGroups()}
            selectedFilters={selectedFilters}
            onFiltersChange={onFiltersChange}
            onAdd={onAdd}
            addButtonLabel="Add Department"
            showAddButton={true}
            showFilters={true}
            showSearch={false}
          />
        }
      />

      {/* Status Toggle Dialog */}
      <StatusToggleDialog
        open={statusDialogOpen}
        onClose={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        item={itemToToggle}
        action={toggleAction}
        module="department"
        loading={statusLoading}
      />
    </Box>
  );
};

export default DepartmentList;
